// ============================================================
// Which app this hostname serves: the YOM dashboard, or a client portal
// on an agency's custom domain (resolved by `?host=`). Pure + tested.
// ============================================================

export const DEFAULT_APP_HOSTS = [
    'localhost',
    '127.0.0.1',
    'yom.aapastech.com',
    'stg-yom.aapastech.com',
    'app.stg.yourownmetrics.aapastech.com',
]

/** VITE_APP_HOSTS ("a.com, b.com") → normalized list; the defaults when unset/empty. */
export function parseAppHosts(raw: string | undefined): string[] {
    const list = (raw ?? '')
        .split(',')
        .map(h => normalizeHost(h))
        .filter(Boolean)
    return list.length > 0 ? list : DEFAULT_APP_HOSTS
}

function normalizeHost(h: string): string {
    let v = h.trim().toLowerCase()
    if (v.startsWith('[')) {
        // [::1]:3000 → [::1]
        v = v.slice(0, v.indexOf(']') + 1) || v
    } else if (v.split(':').length === 2) {
        // host:port (a bare IPv6 address has several colons)
        v = v.replace(/:\d+$/, '')
    }
    return v.replace(/\.$/, '')
}

export type AppMode = { kind: 'dashboard' } | { kind: 'portal-host'; host: string }

/**
 * The dashboard's own hosts (plus loopback, `*.localhost`, Pages preview deployments
 * and anything under aapastech.com) serve the dashboard; any other hostname is a portal custom domain.
 */
export function appModeForHost(hostname: string, appHosts: readonly string[]): AppMode {
    const host = normalizeHost(hostname)
    if (
        !host ||
        appHosts.includes(host) ||
        host === '[::1]' ||
        host === '::1' ||
        host.endsWith('.localhost') ||
        host.endsWith('.pages.dev') ||
        // Our own domain is never a portal custom domain (the API rejects it), so a host
        // missing from VITE_APP_HOSTS can't turn the dashboard into a portal.
        host === 'aapastech.com' ||
        host.endsWith('.aapastech.com')
    ) {
        return { kind: 'dashboard' }
    }
    return { kind: 'portal-host', host }
}
